import OfflineSync from "./OfflineSync";
import OfflineStore from "./OfflineStore";
import { isDeltaSyncDue, isPrecachePrimary } from "./PrecacheConfig";

/**
 * SyncScheduler — Automatic background delta sync for cache-primary mode.
 *
 * Triggers delta syncs based on:
 * - Interval checks (hourly check if sync is due)
 * - Visibility change (when tab becomes visible)
 * - Online event (when reconnecting after offline)
 *
 * Usage:
 *   SyncScheduler.Init(catalogContext, apiKeyContext);
 *   // ... later ...
 *   SyncScheduler.Destroy();
 */
export default class SyncScheduler {
	static #intervalId = null;
	static #catalogContext = null;
	static #apiKeyContext = null;
	static #syncing = false;
	static #manualSyncActive = false; // Tracks if UI-triggered sync is running
	static #CHECK_INTERVAL = 60 * 60 * 1000; // Check every hour

	/**
	 * Initialize the scheduler with contexts.
	 * @param {Object} catalogContext - Catalog context with id, account, pages
	 * @param {Object} apiKeyContext - API key context
	 */
	static #initDelayId = null;
	static #INIT_DELAY = 15000; // Delay initial sync check when cache exists (let UI render first)

	static Init(catalogContext, apiKeyContext) {
		if (!catalogContext?.id || !apiKeyContext) {
			console.warn("[SyncScheduler] Missing context, not initializing");
			return;
		}

		this.#catalogContext = catalogContext;
		this.#apiKeyContext = apiKeyContext;

		// Start interval-based checking
		this.#startInterval();

		// Sync when tab becomes visible (user returns)
		document.addEventListener("visibilitychange", this.#handleVisibilityChange);

		// Sync when coming back online
		window.addEventListener("online", this.#handleOnline);

		// If pages are already cached, delay the initial sync check so it doesn't
		// compete with the UI rendering from cache. Otherwise check immediately.
		isPrecachePrimary(catalogContext.id).then((hasPrecache) => {
			if (hasPrecache) {
				console.warn("[SyncScheduler] Cache exists, deferring initial sync check");
				this.#initDelayId = setTimeout(() => this.#checkAndSync(), this.#INIT_DELAY);
			} else {
				this.#checkAndSync();
			}
		});

		console.warn("[SyncScheduler] Initialized for catalog:", catalogContext.id);
	}

	/**
	 * Clean up the scheduler.
	 */
	static Destroy() {
		if (this.#intervalId) {
			clearInterval(this.#intervalId);
			this.#intervalId = null;
		}
		if (this.#initDelayId) {
			clearTimeout(this.#initDelayId);
			this.#initDelayId = null;
		}
		document.removeEventListener("visibilitychange", this.#handleVisibilityChange);
		window.removeEventListener("online", this.#handleOnline);
		this.#catalogContext = null;
		this.#apiKeyContext = null;
		console.warn("[SyncScheduler] Destroyed");
	}

	/**
	 * Force a delta sync immediately (for manual trigger).
	 * @returns {Promise<{success: boolean, itemCount?: number, error?: Error}>}
	 */
	static async TriggerSync() {
		return this.#runDeltaSync();
	}

	/**
	 * Check if a sync is currently in progress (background or manual).
	 * @returns {boolean}
	 */
	static IsSyncing() {
		return this.#syncing || this.#manualSyncActive;
	}

	/**
	 * Set whether a manual (UI-triggered) sync is active.
	 * Called by OfflineStatusIndicator to prevent concurrent syncs.
	 * @param {boolean} active
	 */
	static SetManualSyncActive(active) {
		this.#manualSyncActive = active;
	}

	// ─── Private Methods ────────────────────────────────────

	static #startInterval() {
		// Check every hour if delta sync is due
		this.#intervalId = setInterval(() => {
			this.#checkAndSync();
		}, this.#CHECK_INTERVAL);
	}

	static #handleVisibilityChange = () => {
		if (document.visibilityState === "visible") {
			// User returned to tab, check if sync needed
			this.#checkAndSync();
		}
	};

	static #handleOnline = () => {
		// Small delay to let connection stabilize
		setTimeout(() => this.#checkAndSync(), 2000);
	};

	static async #checkAndSync() {
		if (this.#syncing || !navigator.onLine) return;
		if (!this.#catalogContext || !this.#apiKeyContext) return;

		const catalogId = this.#catalogContext.id;

		// Only sync if we have a precache (cache-primary mode active)
		const hasPrecache = await isPrecachePrimary(catalogId);
		if (!hasPrecache) return;

		// Check if delta sync is due
		const { isDue } = await isDeltaSyncDue(catalogId);
		if (isDue) {
			console.warn("[SyncScheduler] Delta sync is due, starting background sync...");
			await this.#runDeltaSync();
		}
	}

	static async #runDeltaSync() {
		if (this.#syncing) {
			return { success: false, error: new Error("Sync already in progress") };
		}

		this.#syncing = true;

		// Dispatch start event
		window.dispatchEvent(new CustomEvent("background-sync-start"));

		const sync = new OfflineSync(this.#catalogContext, this.#apiKeyContext);

		try {
			return await new Promise((resolve) => {
				sync.start({
					deltaOnly: true,
					cacheImages: window.matchMedia("(display-mode: standalone)").matches
						|| window.navigator.standalone === true,
					onProgress: (progress) => {
						window.dispatchEvent(new CustomEvent("background-sync-progress", {
							detail: progress,
						}));
					},
					onComplete: ({ itemCount }) => {
						console.warn(`[SyncScheduler] Delta sync complete: ${itemCount} items`);
						window.dispatchEvent(new CustomEvent("background-sync-complete", {
							detail: { itemCount },
						}));
						this.#syncing = false;
						resolve({ success: true, itemCount });
					},
					onError: (error) => {
						console.warn("[SyncScheduler] Delta sync failed:", error);
						window.dispatchEvent(new CustomEvent("background-sync-error", {
							detail: { error: error.message },
						}));
						this.#syncing = false;
						resolve({ success: false, error });
					},
				});
			});
		} catch (error) {
			this.#syncing = false;
			return { success: false, error };
		}
	}
}
